// useXenoarcState.js - Custom hook for Xenoarc bot state management
import { useEffect, useCallback, useMemo, useState, useRef } from "react";
import { getSafeNumericValue, isInProgressDecimal } from "../utils";
import {
  getBotCapital,
  getBotTradeSize,
} from "../components/create-bot-setting/XenoarcComponent";
import { BOT_STRATEGIES } from "../context/botContext";
import {
  initSettingGrid,
  initSettingXenoV2,
} from "../components/drawers/DrawerActivateBot";

export const useXenoarcState = ({
  userBalance,
  selectedBot,
  formXenoV2,
  setFormXenoV2,
  formXenoGrid,
  setFormXenoGrid,
  setSelectedExchange,
  selectedExchange,
  setSelectedPair,
  selectedPair,
  openDialogBot,
  selectedSetting,
  setSelectedSetting,
  setErrors,
  errors,
  actualUserBalance,
  additionalCapital,
  setAdditionalCapital,
}) => {
  const baseCapital = Number(userBalance) || 0;
  const strategy = selectedBot?.bot_name?.toLowerCase();

  const [activeIndex, setActiveIndex] = useState(0);
  const [capitalInput, setCapitalInput] = useState("");
  const [percentage, setPercentage] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialEditCapital, setInitialEditCapital] = useState(0);
  const [systemSettingData, setSystemSettingData] = useState(null);

  // Track previous exchange/pair to detect actual changes
  const prevExchangeRef = useRef(null);
  const prevPairRef = useRef(null);

  // Calculate maximum allowed capital based on mode
  const maxCapital = useMemo(() => {
    // for edit mode
    // if (openDialogBot === "step-bot-2-edit") {
    //   return actualUserBalance + initialEditCapital;
    // }
    return actualUserBalance;
  }, [openDialogBot, actualUserBalance, initialEditCapital]);

  // Calculate percentage for edit mode
  // comment for now cause it now diff
  // useEffect(() => {
  //   if (openDialogBot === "step-bot-2-edit") {
  //     const numCapital = Number(
  //       getBotCapital(strategy, formXenoV2, formXenoGrid),
  //     );
  //     setPercentage((numCapital / maxCapital) * 100);
  //   }
  // }, [maxCapital]);

  // Initialize capitalInput from form data (for EDIT mode)
  // useEffect(() => {
  //   if (openDialogBot === "step-bot-2-edit" && !isInitialized) {
  //     const capital = getBotCapital(strategy, formXenoV2, formXenoGrid);

  //     if (capital && Number(capital) > 0) {
  //       const numCapital = Number(capital);
  //       setCapitalInput(String(capital));
  //       setInitialEditCapital(numCapital);
  //       setIsInitialized(true);
  //     }
  //   }
  // }, [
  //   openDialogBot,
  //   strategy,
  //   formXenoV2?.settings?.amount,
  //   formXenoV2?.settings?.capital,
  //   formXenoGrid?.settings?.amount,
  //   isInitialized,
  // ]);

  const handleCapitalChange = useCallback(
    (e) => {
      let value = e.target.value;

      // Allow comma input (e.g., "22,21")
      value = value.replace(",", ".");

      // Allow empty string or valid numeric input like 12, 12.3, 12.34
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        let num = value === "" ? 0 : parseFloat(value);
        if (isNaN(num)) num = 0;

        // Enforce max capital limit
        if (num > maxCapital && maxCapital > 0) {
          num = maxCapital;
        }

        // Keep what user typed (no forced formatting)
        const displayValue = value === "" ? "" : value;
        setCapitalInput(displayValue);

        // Update percentage
        if (maxCapital > 0) {
          const newPercentage = (num / maxCapital) * 100;
          setPercentage(newPercentage);
        }

        if (openDialogBot === "step-bot-2-edit" && setAdditionalCapital) {
          setAdditionalCapital(num);
        }

        // Determine which setter & key to use
        const setter =
          strategy === BOT_STRATEGIES.GRID ? setFormXenoGrid : setFormXenoV2;
        const key = strategy === BOT_STRATEGIES.GRID ? "amount" : "capital";

        setter((prev) => ({
          ...prev,
          settings: { ...(prev?.settings || {}), [key]: num },
        }));
      }
    },
    [
      strategy,
      setFormXenoGrid,
      setFormXenoV2,
      maxCapital,
      openDialogBot,
      setAdditionalCapital,
    ],
  );

  const handleCapitalBlur = useCallback(() => {
    let numericValue = getSafeNumericValue(capitalInput);
    const min = 5;
    const max = maxCapital;

    if (numericValue < min) numericValue = min;
    if (numericValue > max) numericValue = max;

    setCapitalInput(numericValue.toString());

    const setter =
      strategy === BOT_STRATEGIES.GRID ? setFormXenoGrid : setFormXenoV2;
    const key = strategy === BOT_STRATEGIES.GRID ? "amount" : "capital";

    setter((prev) => ({
      ...prev,
      settings: { ...(prev?.settings || {}), [key]: numericValue },
    }));
  }, [capitalInput, maxCapital, strategy, setFormXenoGrid, setFormXenoV2]);

  const handleSliderChange = useCallback(
    (newPercentage) => {
      if (!maxCapital || maxCapital <= 0) return;

      let newCapital = ((newPercentage / 100) * maxCapital).toFixed(2);
      let numericValue = parseFloat(newCapital);

      // // If editing, don't allow below initial capital
      // if (
      //   openDialogBot === "step-bot-2-edit" &&
      //   numericValue < initialEditCapital
      // ) {
      //   numericValue = initialEditCapital;
      //   newPercentage = (numericValue / maxCapital) * 100;
      // }

      setPercentage(newPercentage);
      setCapitalInput(numericValue.toString());

      if (openDialogBot === "step-bot-2-edit" && setAdditionalCapital) {
        setAdditionalCapital(numericValue);
      }

      const setter =
        strategy === BOT_STRATEGIES.GRID ? setFormXenoGrid : setFormXenoV2;
      const key = strategy === BOT_STRATEGIES.GRID ? "amount" : "capital";

      setter((prev) => ({
        ...prev,
        settings: { ...(prev?.settings || {}), [key]: numericValue },
      }));

      // Auto-adjust trade size if it exceeds new capital
      const tradeSize = parseFloat(
        getBotTradeSize(strategy, formXenoV2, formXenoGrid),
      );
      if (Number(tradeSize) > Number(numericValue)) {
        const tradeSizeKey =
          strategy === BOT_STRATEGIES.GRID ? "trade_size" : "amount";
        setter((prev) => ({
          ...prev,
          settings: { ...(prev?.settings || {}), [tradeSizeKey]: numericValue },
        }));
      }
    },
    [
      maxCapital,
      strategy,
      formXenoV2,
      formXenoGrid,
      setFormXenoGrid,
      setFormXenoV2,
      openDialogBot,
      initialEditCapital,
      setAdditionalCapital,
    ],
  );

  // Auto-adjust if capital exceeds max
  useEffect(() => {
    const numericCapital = Number(capitalInput);
    if (
      !isNaN(numericCapital) &&
      numericCapital > maxCapital &&
      maxCapital > 0
    ) {
      setCapitalInput(String(maxCapital));
      setPercentage(100);
    }
  }, [capitalInput, maxCapital]);

  const handleChangeTradeSize = useCallback(
    (value) => {
      let numericValue = value.replace(/[^0-9.]/g, "");
      const capital = parseFloat(
        getBotCapital(strategy, formXenoV2, formXenoGrid),
      );

      if (!isInProgressDecimal(numericValue)) {
        numericValue = getSafeNumericValue(numericValue, capital);
      }

      const setter =
        strategy === BOT_STRATEGIES.GRID ? setFormXenoGrid : setFormXenoV2;
      const key = strategy === BOT_STRATEGIES.GRID ? "trade_size" : "amount";

      setter((prev) => ({
        ...prev,
        settings: { ...(prev?.settings || {}), [key]: numericValue },
      }));
    },
    [strategy, formXenoV2, formXenoGrid, setFormXenoGrid, setFormXenoV2],
  );

  // Sync capitalInput → form (for CREATE mode and user changes)
  useEffect(() => {
    // if (openDialogBot === "step-bot-2-edit" && !isInitialized) return;
    if (openDialogBot === "step-bot-3-edit") return;

    const capital = Number(capitalInput);
    if (!capitalInput || capitalInput === "") return;

    // Enforce max capital limit
    let finalCapital = capital;
    if (maxCapital > 0 && capital > maxCapital) {
      finalCapital = maxCapital;
      setCapitalInput(String(maxCapital));
      setPercentage(100);
    }

    if (strategy === BOT_STRATEGIES.GRID) {
      setFormXenoGrid((prev) => {
        if (prev?.settings?.amount !== finalCapital) {
          return {
            ...prev,
            settings: { ...(prev?.settings || {}), amount: finalCapital },
          };
        }
        return prev;
      });
    } else if (strategy === BOT_STRATEGIES.XENO_ARC) {
      setFormXenoV2((prev) => {
        if (prev?.settings?.capital !== finalCapital) {
          return {
            ...prev,
            settings: { ...(prev?.settings || {}), capital: finalCapital },
          };
        }
        return prev;
      });
    }
  }, [
    capitalInput,
    strategy,
    openDialogBot,
    isInitialized,
    setFormXenoGrid,
    setFormXenoV2,
    maxCapital,
  ]);

  // Reset form when exchange/pair actually changes (not when navigating steps)
  useEffect(() => {
    // Skip in edit mode
    if (openDialogBot === "step-bot-2-edit") return;

    // Check if exchange or pair actually changed
    const exchangeChanged =
      prevExchangeRef.current !== null &&
      prevExchangeRef.current?.id !== selectedExchange?.id;
    const pairChanged =
      prevPairRef.current !== null &&
      prevPairRef.current?.id !== selectedPair?.id;

    // Update refs
    prevExchangeRef.current = selectedExchange;
    prevPairRef.current = selectedPair;

    // Only reset if there was an actual change (not initial mount or step navigation)
    if (exchangeChanged || pairChanged) {
      setCapitalInput("");
      setPercentage(0);

      // Reset form to initial settings based on strategy
      if (strategy === BOT_STRATEGIES.GRID) {
        setFormXenoGrid((prev) => ({
          ...(prev || {}),
          settings: {
            ...initSettingGrid,
          },
        }));
      } else if (strategy === BOT_STRATEGIES.XENO_ARC) {
        setFormXenoV2((prev) => ({
          ...(prev || {}),
          settings: {
            ...initSettingXenoV2,
          },
        }));
      }
    }
  }, [
    selectedExchange,
    selectedPair,
    openDialogBot,
    strategy,
    setFormXenoGrid,
    setFormXenoV2,
  ]);

  // Reset when drawer closes completely
  useEffect(() => {
    if (!openDialogBot) {
      setSelectedSetting(null);
      setActiveIndex(0);
      setSelectedExchange(null);
      setSelectedPair(null);
      setCapitalInput("");
      setPercentage(0);
      setErrors({});
      setIsInitialized(false);
      setInitialEditCapital(0);
      setSystemSettingData(null);
      prevExchangeRef.current = null;
      prevPairRef.current = null;
    }
  }, [
    openDialogBot,
    setSelectedExchange,
    setSelectedPair,
    setErrors,
    setSelectedSetting,
  ]);

  // Reset when going back to step 1
  useEffect(() => {
    if (openDialogBot === "step-bot-1") {
      setSelectedSetting(null);
      setActiveIndex(0);
      setSelectedExchange(null);
      setSelectedPair(null);
      setCapitalInput("");
      setPercentage(0);
      setErrors({});
      setIsInitialized(false);
      setInitialEditCapital(0);
      setSystemSettingData(null);
      prevExchangeRef.current = null;
      prevPairRef.current = null;
    }
  }, [
    openDialogBot,
    setSelectedExchange,
    setSelectedPair,
    setErrors,
    setSelectedSetting,
  ]);

  // Set custom mode when opening edit
  useEffect(() => {
    if (openDialogBot === "step-bot-2-edit") {
      setSelectedSetting("custom");
    }
  }, [openDialogBot, setSelectedSetting]);

  // Reset settings when switching modes (but not in edit or during navigation)
  useEffect(() => {
    if (!selectedSetting) return;
    if (
      openDialogBot === "step-bot-2-edit" ||
      openDialogBot === "step-bot-2" ||
      openDialogBot === "step-bot-3-edit" ||
      openDialogBot === "step-bot-3"
    )
      return;

    setActiveIndex(0);

    if (strategy === BOT_STRATEGIES.GRID) {
      setFormXenoGrid((prev) => ({
        ...(prev || {}),
        settings: {
          ...initSettingGrid,
          amount: prev?.settings?.amount,
        },
      }));
    }

    if (strategy === BOT_STRATEGIES.XENO_ARC) {
      setFormXenoV2((prev) => ({
        ...(prev || {}),
        settings: {
          ...initSettingXenoV2,
          capital: prev?.settings?.capital,
        },
      }));
    }
  }, [
    selectedSetting,
    strategy,
    openDialogBot,
    setFormXenoGrid,
    setFormXenoV2,
  ]);

  // Clear errors when switching settings
  useEffect(() => {
    setErrors({});
  }, [selectedSetting, setErrors]);

  // Restore capital when returning to step 2
  // useEffect(() => {
  //   if (openDialogBot === "step-bot-2") {
  //     const capt = getBotCapital(strategy, formXenoV2, formXenoGrid);
  //     if (capt && Number(capt) > 0) {
  //       setCapitalInput(String(capt));
  //       const numCapital = Number(capt);
  //       const totalBalance = actualUserBalance + numCapital;
  //       if (totalBalance > 0) {
  //         setPercentage((numCapital / totalBalance) * 100);
  //       }
  //     }
  //   }
  // }, [
  //   openDialogBot,
  //   strategy,
  //   formXenoV2?.settings?.capital,
  //   formXenoGrid?.settings?.amount,
  //   actualUserBalance,
  // ]);

  return {
    strategy,
    baseCapital,
    activeIndex,
    setActiveIndex,
    selectedSetting,
    setSelectedSetting,
    capitalInput,
    percentage,
    handleCapitalChange,
    handleCapitalBlur,
    handleSliderChange,
    handleChangeTradeSize,
    setSystemSettingData,
  };
};
